import { Fragment } from 'react'
import type { MinutePerson } from '../../features/actions/types'
import { groupResponsibles, groupTooltip } from '../../features/principleDecisions/responsibleGroups'


interface Props {
  responsibles: readonly MinutePerson[] | undefined
  /** Kimse yoksa gösterilecek metin; verilmezse hücre boş kalır. */
  emptyText?: string
}

/**
 * Sorumluların **tablo gösterimi** (115): tekil kişiler virgülle yan yana akar, bir isim listesinden
 * gelenler **liste adı altında ve kendi satırında** toplanır; listenin üzerine gelindiğinde gruptaki
 * kişiler görünür.
 *
 * <p><b>Paylaşılan bileşen</b>: aynı gösterim hem bağımsız Prensip Kararları ekranında hem toplantı
 * detayındaki madde/prensip tablolarında gerekiyor (kullanıcı isteği 01.10.2026 — "tabloda gösterim de
 * prensip kararları sayfasındaki gibi olsun"). Dört ayrı yerde elle çizilseydi, ayrımın görünümü
 * yüzeyden yüzeye kayardı. Anayasa gereği `components/ui/` altındadır: önce bir feature klasöründe
 * duruyordu ve başka ekranlarda stilsiz kalmamak için o feature'ın CSS'ini içe aktarmak zorundaydı —
 * yanlış yerde durduğunun doğrudan belirtisi (push kapısı bulgusu 01.10.2026).
 *
 * <p>Dar ekran **kartları** bunu kullanmaz: orada "üzerine gelme" yoktur, bu yüzden `groupLabels`
 * üyeleri parantez içinde düz metin olarak yazar.
 */
export default function ResponsibleGroups({ responsibles, emptyText }: Readonly<Props>) {
  const groups = groupResponsibles(responsibles ?? [])
  if (groups.length === 0) {
    return emptyText ? <span>{emptyText}</span> : null
  }
  return (
    <span className="pd-resp-cell">
      {groups.map((g, i) => {
        // Ayraç JSX'te üretilir, CSS `::before` ile DEĞİL: CSS içeriği metin seçimine/kopyalamaya
        // girmez ve ekran okuyucularda güvenilir okunmaz — mevcut bir test de hücre metnini tek
        // parça beklerken bu yüzden kırılmıştı (01.10.2026). Virgül yalnız YAN YANA akan iki tekil
        // kişi arasında anlamlıdır; liste kendi satırında olduğu için önünde/ardında ayraç olmaz.
        const prev = groups[i - 1]
        const separator = i > 0 && prev.managerListId == null && g.managerListId == null ? ', ' : null
        return (
          <Fragment
            // Tekil kişide anahtar SİCİL'dir, ad değil: aynı ada sahip iki sorumlu gerçek bir
            // senaryo ve ad kullanmak yinelenen React anahtarı üretirdi.
            key={
              g.managerListId == null
                ? `p-${g.people[0]?.employeeId ?? g.label}`
                : `ml-${g.managerListId}`
            }
          >
            {separator}
            {g.managerListId == null ? (
              // Tekil kişi SARMALANMAZ: adı düz metin olarak akar. Her adı bir `<span>`'e koymak
              // hücre metnini DOM'da bölüyor ve "adlar virgülle birleşir" diyen mevcut testi
              // kırıyordu (01.10.2026) — sarmalayıcının tek işlevi liste grubunu işaretlemek.
              g.label
            ) : (
              // `title` SARMALAYICIDA kalır. Bir THY inceleme bulgusu (02.10.2026) onu
              // `aria-hidden` ikona taşımayı önerdi — gerekçe "aynı içerik hem `title`'dan hem
              // gizli metinden gelip ÇİFT duyurulabilir" idi. Taşındı ve push kapısında geri
              // alındı: HTML-AAM'de role'süz bir `<span>`'in `title`'ı erişilebilir ad hesabına
              // GİRMEZ, yani çift duyuru ÖLÇÜLMEMİŞ bir risk; buna karşılık `.pd-resp--list`
              // `cursor:help` + noktalı altçizgiyi TÜM satıra veriyor (ui.css) — ipucunu 11px
              // ikona indirmek, satırın geri kalanını "üzerime gel" deyip hiçbir şey açmayan
              // ölü bir alana çevirirdi. Ölçülmüş gerileme, ölçülmemiş riske tercih edilmez.
              <span className="pd-resp--list" title={groupTooltip(g)}>
                <i className="pi pi-users pd-resp-icon" aria-hidden="true" />
                {g.label}
                {/* Üyeler masaüstünde yalnız `title` ile açılıyordu; klavye ve ekran okuyucu
                    kullanıcısı adları TÜMÜYLE kaybediyordu — oysa önceki gösterim hepsini düz metin
                    veriyordu, yani gerileme olurdu (push kapısı bulgusu 01.10.2026). Görsel olarak
                    gizli metin ikisini birden karşılar. */}
                <span className="ui-sr-only">: {groupTooltip(g)}</span>
              </span>
            )}
          </Fragment>
        )
      })}
    </span>
  )
}
